import { buyerEmailState, usableBuyerEmail } from '@platform/domain';
import { FlaskConical, Mail, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { Draft, EmailTransport } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { Button } from '../../../ui/Button';
import { RadioCards } from '../../../ui/Choice';
import styles from '../Sales.module.css';

/** The step-4 choice (WP-1b.4, BLOCK-1B-EMAIL-DESIGN §2): no copy, or a PDF copy by e-mail. */
export type MailChoiceValue = 'none' | 'send';

/** What the block can offer for the chosen buyer (§2.2). */
export type MailState = 'available' | 'missing' | 'invalid' | 'cash' | 'no-buyer';

export interface MailTarget {
  state: MailState;
  /** The buyer record's «بريد الفواتير» (the normalised address when usable). */
  recipient: string | null;
}

/**
 * The block's state from the draft's buyer and the buyer record, with the API's own address rule
 * (`@platform/domain`, the parties schema), so the web and the API never disagree. Null while the
 * buyer record is still loading.
 */
export function mailTargetOf(
  receiver: Draft['receiver'] | undefined,
  party: { email: string | null } | null | undefined,
): MailTarget | null {
  if (!receiver) return { state: 'no-buyer', recipient: null };
  if ('cash' in receiver) return { state: 'cash', recipient: null };
  if (!party) return null;
  const state = buyerEmailState(party.email);
  if (state === 'available') return { state, recipient: usableBuyerEmail(party.email) };
  return { state, recipient: state === 'invalid' ? party.email : null };
}

/**
 * The choice as editor state, never a draft field (M-4): it starts as given (a new draft's hand-off
 * keeps it across the first save) and goes back to «لا تُرسِل نسخة» whenever the recipient changes —
 * another buyer, an edited address, the unidentified buyer — calling `onReset` when "send" was on.
 */
export function useMailChoice(
  initial: MailChoiceValue,
  target: MailTarget | null,
  onReset: () => void,
): [MailChoiceValue, (value: MailChoiceValue) => void] {
  const [choice, setChoice] = useState<MailChoiceValue>(initial);
  const key = target ? `${target.state}|${target.recipient ?? ''}` : null;
  const last = useRef<string | null>(key);
  const latest = useRef({ choice, onReset });
  latest.current = { choice, onReset };
  useEffect(() => {
    if (key === null) return; // the buyer record is loading: nothing is known yet
    const before = last.current;
    last.current = key;
    if (before === null || before === key) return;
    if (latest.current.choice === 'send') {
      setChoice('none');
      latest.current.onReset();
    }
  }, [key]);
  return [choice, setChoice];
}

/**
 * «نسخة للمشتري بالبريد», after the three check sections of step 4 and before the «للاختبار فقط» box
 * (M-1). A neutral bordered box with no count and no «عُد إلى …», so it never reads as a fourth
 * check. With a usable address: the address read-only and two named outcomes, «لا تُرسِل نسخة»
 * selected on entry (M-2, M-3), never a checkbox or a switch. Without one: a sentence and no control;
 * for a missing or invalid address, the button to the buyer step (hidden without `customer.edit`).
 */
export function MailChoice({
  target,
  choice,
  onChoice,
  transport,
  canEditBuyer,
  onFixBuyer,
}: {
  target: MailTarget | null;
  choice: MailChoiceValue;
  onChoice: (value: MailChoiceValue) => void;
  transport: EmailTransport | undefined;
  canEditBuyer: boolean;
  /** Opens the buyer step with the buyer form in edit mode and its e-mail field focused. */
  onFixBuyer: () => void;
}) {
  const { t } = useI18n();
  const titleId = useId();
  const leadId = useId();
  if (!target) return null;
  const m = t.editor.mail;
  const sentence: Record<Exclude<MailState, 'available'>, string> = {
    missing: m.missing,
    invalid: m.invalid,
    cash: m.cash,
    'no-buyer': m.noBuyer,
  };
  return (
    <section
      className={styles.mailBlock}
      aria-labelledby={titleId}
      data-testid="mail-choice"
      data-state={target.state}
      data-choice={target.state === 'available' ? choice : 'none'}
    >
      <h3 id={titleId} className={styles.reviewTitle}>
        <Mail size={16} aria-hidden="true" /> {m.title}
      </h3>
      {target.state === 'available' ? (
        <>
          <p id={leadId} className={styles.mailLead}>
            {m.lead}
          </p>
          <dl className={styles.reviewList}>
            <div className={styles.reviewPair}>
              <dt>{t.parties.email}</dt>
              <dd>
                <span className={styles.mailAddress} dir="ltr" data-testid="mail-recipient">
                  {target.recipient}
                </span>
                {canEditBuyer ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={styles.textButton}
                    onPress={onFixBuyer}
                    testId="mail-change-buyer"
                  >
                    {m.change}
                  </Button>
                ) : null}
              </dd>
            </div>
          </dl>
          <RadioCards<MailChoiceValue>
            aria-labelledby={titleId}
            aria-describedby={leadId}
            fieldName="mailChoice"
            testId="mail-options"
            value={choice}
            onChange={onChoice}
            className={styles.mailOptions}
            options={[
              { value: 'none', title: m.none, description: m.noneHint, testId: 'mail-option-none' },
              { value: 'send', title: m.send, description: m.sendHint, testId: 'mail-option-send' },
            ]}
          />
          {transport === 'file' ? (
            <p className={styles.mailTestNote} data-testid="mail-test-note">
              <FlaskConical size={14} aria-hidden="true" /> <span>{m.testNote}</span>
            </p>
          ) : null}
        </>
      ) : (
        <>
          {target.state === 'invalid' && target.recipient ? (
            <p className={styles.mailInvalid}>
              <TriangleAlert size={14} aria-hidden="true" />
              <span className={styles.mailAddress} dir="ltr" data-testid="mail-recipient">
                {target.recipient}
              </span>
            </p>
          ) : null}
          <p className={styles.mailNote} data-testid="mail-note">
            {sentence[target.state]}
          </p>
          {(target.state === 'missing' || target.state === 'invalid') && canEditBuyer ? (
            <div>
              <Button variant="secondary" size="sm" onPress={onFixBuyer} testId="mail-fix-buyer">
                {m.fix}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
